import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaChevronDown, FaChevronUp, FaBriefcase, FaInfo, FaHome, FaEnvelope, FaVial, FaUsers, FaNewspaper, FaHeartbeat } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const SidebarAdmin: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    const path = location.pathname;
    const shouldOpen = {
      home: path.startsWith("/admin/home"),
      whoWeAre: path.startsWith("/admin/who-we-are"),
      careers: path.startsWith("/admin/careers"),
      products: path.startsWith("/admin/products"),
      medicalProducts: path.includes("/otc"),
      cbd: path.includes("/cbd"),
      dietary: path.includes("/dietary-supplements"),
      personalCare: path.includes("/personal-care"),
    };
    setOpenMenus(prev => ({ ...prev, ...shouldOpen }));
  }, [location.pathname]);


  const toggleMenu = (key: string) => {
    setOpenMenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (path: string) => location.pathname === path;
  const cosmeticSubcategories = [
    { key: "BABYGAL Baby Skin Care" },
    { key: "FACE & LIP CARE" },
    { key: "SENSILIS MAM CARE" },
    { key: "SENSILIS FOOT CARE" },
    { key: "SENSILIS NAIL & HAND CARE" },
    { key: "SENSILIS OILS" },
    { key: "SILK HAIR REMOVAL" },
    { key: "SOLARUS SUN PROTECTION CARE" },
    { key: "CITROGALA INSPECT PROTECTION" }
  ];

  const SidebarLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
    <Link
      to={to}
      className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${isActive(to) ? "bg-brand-50 text-brand-700 font-semibold" : "text-brand-gray hover:bg-brand-50 hover:text-brand-600"
        }`}
    >
      {children}
    </Link>
  );

  const CollapsibleMenu = ({
    label,
    icon,
    menuKey,
    children,
  }: {
    label: string;
    icon: React.ReactNode;
    menuKey: string;
    children: React.ReactNode;
  }) => (
    <li>
      <div
        onClick={() => toggleMenu(menuKey)}
        className="flex items-center justify-between px-4 py-2 rounded-lg cursor-pointer transition-colors text-brand-gray hover:bg-brand-50 hover:text-brand-600"
      >
        <div className="flex items-center gap-3 font-medium">
          {icon}
          <span>{label}</span>
        </div>
        {openMenus[menuKey] ? <FaChevronUp className="text-brand-400 text-sm" /> : <FaChevronDown className="text-brand-400 text-sm" />}
      </div>
      {openMenus[menuKey] && <ul className="ml-4 mt-2 space-y-2 text-sm">{children}</ul>}
    </li>
  );

  const CollapsibleSubmenu = ({
    label,
    submenuKey,
    links,
  }: {
    label: string;
    submenuKey: string;
    links: { label: string; to: string }[];
  }) => (
    <li>
      <div
        onClick={() => toggleMenu(submenuKey)}
        className="flex items-center justify-between px-4 py-2 rounded-lg cursor-pointer transition-colors text-brand-gray hover:bg-brand-50 hover:text-brand-600">
        <span className="font-medium">{label}</span>
        {openMenus[submenuKey] ? <FaChevronUp className="text-brand-400 text-sm" /> : <FaChevronDown className="text-brand-400 text-sm" />}
      </div>
      {openMenus[submenuKey] && (
        <ul className="ml-4 mt-1 space-y-1">
          {links.map(link => (
            <li key={link.to}>
              <Link
                to={link.to}
                className={`block px-4 py-2 rounded-lg transition-colors ${isActive(link.to) ? "bg-brand-50 text-brand-700 font-semibold" : "text-brand-gray hover:bg-brand-50 hover:text-brand-600"
                  }`}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );

  return (
    <div className="w-80 h-screen bg-white text-brand-gray fixed top-0 left-0 flex flex-col p-6 shadow-sm border-r border-gray-100 overflow-y-auto font-montserrat">
      <h2 className="text-2xl font-bold mb-6">
         {/* DEMO VERSION  */}
      {/* {t(`admin.adminPanel`)} */}
         </h2>
      <ul className="space-y-4 text-base text-lg">
        <li>
          <SidebarLink to="/admin/users">
            <FaUsers className="text-brand-500" />
            <span>  {t(`users.users`)}</span>
          </SidebarLink>
        </li>
        <CollapsibleMenu label={t('navbar.home')} icon={<FaHome className="text-brand-500" />} menuKey="home">
          <li>
            <Link
              to="/admin/home/dosage"
              className={`block px-4 py-2 rounded-lg transition-colors ${isActive("/admin/home/dosage") ? "bg-brand-50 text-brand-700 font-semibold" : "text-brand-gray hover:bg-brand-50 hover:text-brand-600"
                }`}
            >
              {t(`admin.dosage`)}
            </Link>
          </li>
        </CollapsibleMenu>
        <CollapsibleMenu label={t('navbar.whoWeAre')} icon={<FaInfo className="text-brand-500" />} menuKey="whoWeAre" >
          <li>
            <Link
              to="/admin/who-we-are/about"
              className={`block px-4 py-2 rounded-lg transition-colors ${isActive("/admin/who-we-are/about") ? "bg-brand-50 text-brand-700 font-semibold" : "text-brand-gray hover:bg-brand-50 hover:text-brand-600"
                }`}
            >
              {t(`navbar.aboutUs`)}
            </Link>
          </li>
          <li>
            <Link
              to="/admin/who-we-are/history"
              className={`block px-4 py-2 rounded-lg transition-colors ${isActive("/admin/who-we-are/history") ? "bg-brand-50 text-brand-700 font-semibold" : "text-brand-gray hover:bg-brand-50 hover:text-brand-600"
                }`}
            >
              {t(`navbar.history`)}
            </Link>
          </li>
        </CollapsibleMenu>
        <CollapsibleMenu label={t('admin.products')} icon={<FaVial className="text-brand-500" />} menuKey="products">
          <CollapsibleSubmenu
            label={t('products.medicinal')}
            submenuKey="medicalProducts"
            links={[
              { label: t('subcategories.OTC'), to: "/admin/products/medical-products/otc" },

            ]}
          />
          <CollapsibleSubmenu
            label={t('products.cbd')}
            submenuKey="cbd"
            links={[
              { label: t('subcategories.Oral'), to: "/admin/products/cbd/oral" },
              { label: t('subcategories.Topical'), to: "/admin/products/cbd/topical" },
              { label: t('subcategories.Cosmetics'), to: "/admin/products/cbd/cosmetics" },
            ]}
          />
          <CollapsibleSubmenu
            label={t('products.dietary')}
            submenuKey="dietary"
            links={[
              { label: "JEKOGAL", to: "/admin/products/dietary-supplements/jekogal" },
              { label: "GALAHEALTH", to: "/admin/products/dietary-supplements/galahealth" },
              { label: "GALAPRO", to: "/admin/products/dietary-supplements/galapro" },
            ]}
          />
          <li>
            <Link
              to="/admin/products/herbal-products"
              className={`block px-4 py-2 rounded-lg transition-colors ${isActive("/admin/products/herbal-products") ? "bg-brand-50 text-brand-700 font-semibold" : "text-brand-gray hover:bg-brand-50 hover:text-brand-600"
                }`}
            >
              {t(`products.herbal`)}
            </Link>
          </li>
          <CollapsibleSubmenu
            label={t('products.personalCare')}
            submenuKey="personalCare"
            links={cosmeticSubcategories.map(sub => ({
              label: t(`subcategories.${sub.key}`), // Translated
              to: `/admin/products/personal-care/${encodeURIComponent(
                sub.key.toLowerCase().replace(/ &| /g, "-")
              )}` // Static English-based URL
            }))}
          />
        </CollapsibleMenu>
        <CollapsibleMenu label={t('navbar.careers')} icon={<FaBriefcase className="text-brand-500" />} menuKey="careers" >
          <li>
            <Link
              to="/admin/careers/vacancy"
              className={`block px-4 py-2 rounded-lg transition-colors ${isActive("/admin/careers/vacancy") ? "bg-brand-50 text-brand-700 font-semibold" : "text-brand-gray hover:bg-brand-50 hover:text-brand-600"
                }`}
            >
              {t(`admin.vacancy`)}
            </Link>
          </li>
        </CollapsibleMenu>
        <li>
          <SidebarLink to="/admin/news">
            <FaNewspaper className="text-brand-500" />
            <span>  {t(`navbar.news`)}</span>
          </SidebarLink>
        </li>
        <li>
          <SidebarLink to="/admin/blog">
            <FaHeartbeat className="text-brand-500" />
            <span>  {t(`navbar.health`)}</span>
          </SidebarLink>
        </li>
        <li>
          <SidebarLink to="/admin/contacts">
            <FaEnvelope className="text-brand-500" />
            <span>  {t(`navbar.contacts`)}</span>
          </SidebarLink>
        </li>
      </ul>
    </div>
  );
};

export default SidebarAdmin;
